import React, { FC, useState, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { isEqual } from 'lodash';
import { Autocomplete, Button, Dialog, FormControl, TextField, FormLabel } from '@mui/material';
import { IActionRewardCampaign, INewActionRewardCampaign } from 'reduxState/store/actionRewards/type';
import { Clear } from '@mui/icons-material';
import { DateTimeRangePicker } from 'components/DateTimeRangePickerLocal/DateTimeRangePickerLocal';
import { Application } from 'reduxState/store/application/types';
import { IAppOption, TCampaignTypes, IMultiExtensionConfig } from './types';
import { getAppOptions } from 'components/BoostedOffers/helpers';
import { campaignTypes } from './constants';
import {
  useCreateActionRewardCampaignsMutation,
  useUpdateActionRewardCampaignsMutation,
} from 'reduxState/store/actionRewards/api';
import { getCampaignStatus, toISOStringWithoutMs, friendlyDisplayToJson, jsonToFriendlyDisplay } from './helpers';
import { formatLocalDateTime } from 'helpers/DateHelpers';

interface IActionRewardsModal {
  applications: Application[];
  isModalOpen: boolean;
  selectedCampaign: IActionRewardCampaign | null;
  handleCloseModal: () => void;
}

/**
 * ActionRewardsModal component provides a modal dialog for creating or editing Action Reward Campaigns.
 *
 * Props:
 * @param {Application[]} applications - List of available applications to select for the campaign.
 * @param {boolean} isModalOpen - Controls whether the modal is open or closed.
 * @param {IActionRewardCampaign | null} selectedCampaign - The campaign to edit, or null for creating a new campaign.
 * @param {() => void} handleCloseModal - Function to close the modal dialog.
 *
 * @description
 * In "create" mode, allows the user to fill out all required fields to create a new campaign.
 * In "edit" mode, pre-fills the form with the selected campaign's data and allows editing if the campaign is present or future.
 * Validates required fields and only enables submission when the form is ready.
 * Handles both creation and update of campaigns via API mutations.
 *
 * @example
 * <ActionRewardsModal
 *   applications={applications}
 *   isModalOpen={isModalOpen}
 *   selectedCampaign={selectedCampaign}
 *   handleCloseModal={handleCloseModal}
 * />
 */
const ActionRewardsModal: FC<IActionRewardsModal> = ({
  applications,
  isModalOpen,
  selectedCampaign,
  handleCloseModal,
}) => {
  const isInEditMode = !!selectedCampaign;
  const campaignStatus = isInEditMode ? getCampaignStatus(selectedCampaign?.startDate, selectedCampaign?.endDate) : '';
  const isPresentCampaign = isInEditMode && campaignStatus === 'present';

  const initialSelectedApp = useMemo(() => {
    if (selectedCampaign?.applicationID) {
      const foundApp = applications.find(app => Number(app.id) === selectedCampaign.applicationID);
      return foundApp ? [{ label: `${foundApp.id} - ${foundApp.name}`, id: foundApp.id }] : [];
    }
    return [];
  }, [selectedCampaign?.applicationID, applications]);

  const initialFirstCommissionConfig = useMemo(() => {
    if (
      selectedCampaign?.config &&
      (selectedCampaign.actionReward === 'FIRST_COMMISSION' ||
        selectedCampaign.actionReward === 'UUID_FIRST_COMMISSION')
    ) {
      try {
        const config = JSON.parse(selectedCampaign.config);
        return config.days || '';
      } catch {
        return '';
      }
    }
    return '';
  }, [selectedCampaign?.config, selectedCampaign?.actionReward]);

  const initialFirstCommissionPostCampaignDays = useMemo(() => {
    if (selectedCampaign?.config && selectedCampaign.actionReward === 'FIRST_COMMISSION') {
      try {
        const config = JSON.parse(selectedCampaign.config);
        return config.post_campaign_purchase_window_days ? String(config.post_campaign_purchase_window_days) : '';
      } catch {
        return '';
      }
    }
    return '';
  }, [selectedCampaign?.config, selectedCampaign?.actionReward]);

  const initialCampaignCode = useMemo(() => {
    return selectedCampaign?.campaignCode || '';
  }, [selectedCampaign?.campaignCode]);

  const initialMultiExtensionConfig = useMemo(() => {
    if (selectedCampaign?.config && selectedCampaign.actionReward === 'MULTI_EXTENSION_ENROLLMENT') {
      const config = JSON.parse(selectedCampaign.config);
      return config;
    }
    return {
      enrollment_application_id: '',
      enrollment_period_start: '',
      enrollment_period_end: '',
      max_days_since_enrollment: '',
    };
  }, [selectedCampaign?.config, selectedCampaign?.actionReward]);

  const initialSegmentCommissionConfig = useMemo(() => {
    if (selectedCampaign?.config && selectedCampaign.actionReward === 'SEGMENT_COMMISSION') {
      try {
        const config = JSON.parse(selectedCampaign.config);
        return config.min_days_since_enrollment || '';
      } catch {
        return '';
      }
    }
    return '';
  }, [selectedCampaign?.config, selectedCampaign?.actionReward]);

  const initialDormantPurchaserConfig = useMemo(() => {
    if (selectedCampaign?.config && selectedCampaign.actionReward === 'DORMANT_PURCHASER') {
      try {
        const config = JSON.parse(selectedCampaign.config);
        return config.min_days_since_last_commission || '';
      } catch {
        return '';
      }
    }
    return '';
  }, [selectedCampaign?.config, selectedCampaign?.actionReward]);

  const [selectedApps, setSelectedApps] = useState<IAppOption[]>(initialSelectedApp);
  const [actionReward, setActionReward] = useState<TCampaignTypes | ''>(
    selectedCampaign?.actionReward as TCampaignTypes | '',
  );
  const [bonusAmount, setBonusAmount] = useState<string>(selectedCampaign?.bonusAmount || '');
  const [startDate, setStartDate] = useState<Date | null>(
    selectedCampaign?.startDate ? new Date(selectedCampaign.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(
    selectedCampaign?.endDate ? new Date(selectedCampaign.endDate) : null,
  );
  const [firstCommissionConfig, setFirstCommissionConfig] = useState<string>(initialFirstCommissionConfig);
  const [firstCommissionPostCampaignDays, setFirstCommissionPostCampaignDays] = useState<string>(
    initialFirstCommissionPostCampaignDays,
  );
  const [campaignCode, setCampaignCode] = useState<string>(initialCampaignCode);
  const [multiExtensionConfig, setMultiExtensionConfig] = useState<IMultiExtensionConfig>(initialMultiExtensionConfig);
  const [segmentCommissionConfig, setSegmentCommissionConfig] = useState<string>(initialSegmentCommissionConfig);
  const [dormantPurchaserConfig, setDormantPurchaserConfig] = useState<string>(initialDormantPurchaserConfig);

  const { user } = useAuth0();
  const [createActionRewardCampaigns, { isLoading: isCreatingCampaign }] = useCreateActionRewardCampaignsMutation();
  const [updateActionRewardCampaigns, { isLoading: isUpdatingCampaign }] = useUpdateActionRewardCampaignsMutation();
  const isFormReady = useMemo(() => {
    const isSegmentConfigValid = !!segmentCommissionConfig && /^[1-9][0-9]*$/.test(segmentCommissionConfig);
    const isDormantConfigValid = !!dormantPurchaserConfig && /^[1-9][0-9]*$/.test(dormantPurchaserConfig);
    // In edit mode, check if any field has changed
    if (isInEditMode && selectedCampaign) {
      const anyFieldChanged =
        selectedApps.length !== initialSelectedApp.length ||
        (selectedApps.length > 0 && selectedApps[0].id !== initialSelectedApp[0]?.id) ||
        actionReward !== selectedCampaign.actionReward ||
        bonusAmount !== selectedCampaign.bonusAmount ||
        toISOStringWithoutMs(startDate!) !== selectedCampaign.startDate ||
        toISOStringWithoutMs(endDate!) !== selectedCampaign.endDate ||
        (actionReward === 'FIRST_COMMISSION' &&
          !isEqual(
            [
              `Days: ${firstCommissionConfig}`,
              ...(firstCommissionPostCampaignDays
                ? [`Post Campaign Purchase Window Days: ${firstCommissionPostCampaignDays}`]
                : []),
            ].join('\n'),
            jsonToFriendlyDisplay(selectedCampaign.config),
          )) ||
        (actionReward === 'UUID_FIRST_COMMISSION' &&
          (campaignCode !== initialCampaignCode || firstCommissionConfig !== initialFirstCommissionConfig)) ||
        (actionReward === 'MULTI_EXTENSION_ENROLLMENT' &&
          !isEqual(multiExtensionConfig, JSON.parse(selectedCampaign.config))) ||
        (actionReward === 'SEGMENT_COMMISSION' && segmentCommissionConfig !== initialSegmentCommissionConfig) ||
        (actionReward === 'DORMANT_PURCHASER' && dormantPurchaserConfig !== initialDormantPurchaserConfig);
      const isUuidConfigValid = actionReward !== 'UUID_FIRST_COMMISSION' || (!!campaignCode.trim() && !!firstCommissionConfig);
      const isMultiExtensionConfigValid =
        actionReward !== 'MULTI_EXTENSION_ENROLLMENT' ||
        (!!multiExtensionConfig.enrollment_application_id &&
          !!multiExtensionConfig.enrollment_period_start &&
          !!multiExtensionConfig.enrollment_period_end &&
          !!multiExtensionConfig.max_days_since_enrollment);
      return (
        anyFieldChanged &&
        (actionReward !== 'SEGMENT_COMMISSION' || isSegmentConfigValid) &&
        (actionReward !== 'DORMANT_PURCHASER' || isDormantConfigValid) &&
        isUuidConfigValid &&
        isMultiExtensionConfigValid
      );
    } else {
      // In create mode, check if any required field is filled
      return (
        selectedApps.length > 0 &&
        !!actionReward &&
        !!bonusAmount &&
        !!startDate &&
        !!endDate &&
        (actionReward !== 'FIRST_COMMISSION' || !!firstCommissionConfig) &&
        (actionReward !== 'UUID_FIRST_COMMISSION' || (!!campaignCode.trim() && !!firstCommissionConfig)) &&
        (actionReward !== 'MULTI_EXTENSION_ENROLLMENT' ||
          (!!multiExtensionConfig.enrollment_application_id &&
            !!multiExtensionConfig.enrollment_period_start &&
            !!multiExtensionConfig.enrollment_period_end &&
            !!multiExtensionConfig.max_days_since_enrollment)) &&
        (actionReward !== 'SEGMENT_COMMISSION' || isSegmentConfigValid) &&
        (actionReward !== 'DORMANT_PURCHASER' || isDormantConfigValid)
      );
    }
  }, [
    isInEditMode,
    selectedApps,
    initialSelectedApp,
    actionReward,
    bonusAmount,
    startDate,
    endDate,
    firstCommissionConfig,
    firstCommissionPostCampaignDays,
    campaignCode,
    initialCampaignCode,
    initialFirstCommissionConfig,
    multiExtensionConfig,
    segmentCommissionConfig,
    initialSegmentCommissionConfig,
    dormantPurchaserConfig,
    initialDormantPurchaserConfig,
    selectedCampaign,
  ]);

  const getConfig = () => {
    if (actionReward === 'FIRST_COMMISSION') {
      const lines = [`Days: ${firstCommissionConfig}`];
      if (firstCommissionPostCampaignDays) {
        lines.push(`Post Campaign Purchase Window Days: ${firstCommissionPostCampaignDays}`);
      }
      return friendlyDisplayToJson(lines.join('\n'));
    } else if (actionReward === 'UUID_FIRST_COMMISSION') {
      return JSON.stringify({ days: firstCommissionConfig });
    } else if (actionReward === 'MULTI_EXTENSION_ENROLLMENT') {
      return JSON.stringify(multiExtensionConfig);
    } else if (actionReward === 'SEGMENT_COMMISSION') {
      return JSON.stringify({ min_days_since_enrollment: segmentCommissionConfig });
    } else if (actionReward === 'DORMANT_PURCHASER') {
      return JSON.stringify({ min_days_since_last_commission: dormantPurchaserConfig });
    } else {
      return '{}';
    }
  };

  // Helper function for numeric input validation
  const handleNumericChange = (value: string, setter: (val: string) => void, allowDecimals = false) => {
    const regex = allowDecimals ? /^[0-9]*\.?[0-9]*$/ : /^[0-9]*$/;
    if (regex.test(value)) {
      setter(value);
    }
  };

  const handleClearForm = () => {
    setSelectedApps([]);
    setActionReward('');
    setBonusAmount('');
    setStartDate(null);
    setEndDate(null);
    setFirstCommissionConfig('');
    setFirstCommissionPostCampaignDays('');
    setCampaignCode('');
    setMultiExtensionConfig({
      enrollment_application_id: '',
      enrollment_period_start: '',
      enrollment_period_end: '',
      max_days_since_enrollment: '',
    });
    setSegmentCommissionConfig('');
    setDormantPurchaserConfig('');
  };

  const handleCreateCampaign = async () => {
    selectedApps.forEach(async app => {
      const newCampaign: INewActionRewardCampaign = {
        actionReward,
        bonusAmount,
        config: getConfig(),
        applicationID: Number(app.id),
        startDate: startDate?.toISOString() || '',
        endDate: endDate?.toISOString() || '',
        modifiedAuthor: user?.email || 'Unknown author',
        currencyId: 840, // TODO: Currently always sets to USD but this should be dynamic
        ...(actionReward === 'UUID_FIRST_COMMISSION' && { campaignCode: campaignCode.trim() }),
      };

      try {
        await createActionRewardCampaigns({
          campaigns: newCampaign,
        }).unwrap();
        handleCloseModal();
        handleClearForm();
        toast.success('Campaign was created successfully!');
      } catch (error) {
        console.error(error);
        toast.error('Failed to create campaign. Check the dates and try again.');
      }
    });
  };

  const handleUpdateCampaign = async () => {
    try {
      await updateActionRewardCampaigns({
        campaigns: {
          ...selectedCampaign,
          applicationID: Number(selectedApps[0].id),
          actionReward,
          bonusAmount,
          config: getConfig(),
          startDate: startDate?.toISOString() || '',
          endDate: endDate?.toISOString() || '',
          modifiedAuthor: user?.email || 'Unknown author',
          currencyId: 840, // TODO: Currently always sets to USD but this should be dynamic
          campaignCode: actionReward === 'UUID_FIRST_COMMISSION' ? campaignCode.trim() : undefined,
        },
        campaignId: selectedCampaign?.id || 0,
      }).unwrap();
      handleCloseModal();
      handleClearForm();
      toast.success('Campaign was updated successfully!');
    } catch (error) {
      console.error(error);
      toast.error('Failed to update campaign. Please try again.');
    }
  };

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleCloseModal}
      classes={{ paper: 'px-[80px] py-[50px] w-[1400px] min-w-[500px]' }}
      fullWidth={true}
      maxWidth="md"
    >
      <div
        className="absolute top-[15px] right-[15px] cursor-pointer"
        onClick={handleCloseModal}
        data-testid="close-button"
      >
        <Clear />
      </div>
      <div className="flex flex-row justify-between w-full flex-wrap ">
        <h3 className="m-0">{isInEditMode ? 'Edit Action Reward Campaign' : 'Create Action Reward Campaign'}</h3>
        <div className="w-full mt-8 flex flex-wrap gap-4 lg:flex-row lg:!flex-nowrap">
          <Autocomplete
            multiple
            disableCloseOnSelect
            disabled={isPresentCampaign}
            size="medium"
            className="w-full lg:grow bg-white rounded-md max-height-full overflow-auto font-montserrat lg:min-w-[250px] [&>div>div>input]:!font-montserrat [&>div>div>input]:!text-sm [&>div>div]:!rounded-md"
            value={selectedApps}
            options={getAppOptions(applications)}
            isOptionEqualToValue={(option, value) => option.id === value.id} // To prevent from getting a MUI warning when option and value are pointing to different instances
            onChange={(_, value) => setSelectedApps(value)}
            sx={{
              '& .MuiAutocomplete-tag': {
                background: '#5c5387',
                color: '#fff',
                fontSize: '11px',

                '& .MuiChip-deleteIcon': {
                  color: '#fff',
                  opacity: '0.7',

                  '&:hover': {
                    color: '#fff',
                    opacity: '0.9',
                  },
                },
              },
            }}
            renderInput={params => (
              <TextField
                {...params}
                className="flex items-center flex-row font-montserrat shadow-md rounded-md"
                variant="outlined"
                placeholder="Applications to Include"
              />
            )}
          />
          <Autocomplete
            className="w-full lg:min-w-[200px] lg:w-auto [&>div>div>input]:!font-montserrat [&>div>div>input]:!text-sm [&>div>div]:!rounded-md"
            size="medium"
            disabled={isPresentCampaign}
            value={campaignTypes.find(type => type.value === actionReward) || null}
            options={campaignTypes}
            onChange={(_, data) => setActionReward(data?.value as TCampaignTypes)}
            renderInput={params => <TextField {...params} placeholder="Campaign Type" />}
          />
          <TextField
            className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
            size="medium"
            label="Bonus Amount"
            disabled={isPresentCampaign}
            value={bonusAmount}
            onChange={e => handleNumericChange(e.target.value, setBonusAmount, true)}
            placeholder="Bonus Amount"
          />
        </div>
        {actionReward === 'MULTI_EXTENSION_ENROLLMENT' && (
          <FormControl className="mt-5 mb-2">
            <FormLabel component="legend" className="text-sm font-montserrat">
              Configuration
            </FormLabel>
            <div className="w-full flex flex-col gap-4 !flex-row flex-wrap lg:flex-nowrap p-4 border border-gray-300 rounded-md">
              <DateTimeRangePicker
                includeTime={false}
                startDateLabel="Period Start"
                endDateLabel="Period End"
                hasMinDate={false}
                className="w-full !mt-0 justify-end gap-4 [&>div>.MuiInputBase-root>input]:!font-montserrat [&>div>.MuiInputBase-root>input]:!text-sm [&>div>.MuiFormLabel-root]:!font-montserrat [&>div>.MuiFormLabel-root]:!text-sm [&>div]:!w-full flex-nowrap"
                startDate={
                  multiExtensionConfig.enrollment_period_start
                    ? new Date(multiExtensionConfig.enrollment_period_start)
                    : null
                }
                endDate={
                  multiExtensionConfig.enrollment_period_end
                    ? new Date(multiExtensionConfig.enrollment_period_end)
                    : null
                }
                onStartDateChange={value => {
                  setMultiExtensionConfig({
                    ...multiExtensionConfig,
                    enrollment_period_start: value.toISOString().split('T')[0],
                  });
                }}
                onEndDateChange={value => {
                  setMultiExtensionConfig({
                    ...multiExtensionConfig,
                    enrollment_period_end: value.toISOString().split('T')[0],
                  });
                }}
              />
              <div className="w-full flex gap-4 flex-nowrap">
                <TextField
                  className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
                  size="medium"
                  label="Application ID"
                  value={multiExtensionConfig.enrollment_application_id}
                  placeholder="Enter ID"
                  onChange={e =>
                    handleNumericChange(e.target.value, value =>
                      setMultiExtensionConfig({ ...multiExtensionConfig, enrollment_application_id: value }),
                    )
                  }
                />
                <TextField
                  className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
                  size="medium"
                  label="Max Days Since Enrollment"
                  value={multiExtensionConfig.max_days_since_enrollment}
                  placeholder="Enter Max Days"
                  onChange={e =>
                    handleNumericChange(e.target.value, value =>
                      setMultiExtensionConfig({ ...multiExtensionConfig, max_days_since_enrollment: value }),
                    )
                  }
                />
              </div>
            </div>
          </FormControl>
        )}
        {actionReward === 'UUID_FIRST_COMMISSION' && (
          <FormControl className="mt-5 mb-2 w-full">
            <FormLabel component="legend" className="text-sm font-montserrat">
              Configuration
            </FormLabel>
            <div className="w-full flex gap-4 flex-wrap lg:flex-nowrap p-4 border border-gray-300 rounded-md">
              <TextField
                className="w-full lg:min-w-[200px] lg:w-auto text-field-label"
                size="medium"
                label="Campaign Code"
                value={campaignCode}
                placeholder="Enter Campaign Code"
                onChange={e => setCampaignCode(e.target.value)}
              />
              <TextField
                className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
                size="medium"
                label="Days Config"
                value={firstCommissionConfig}
                placeholder="Enter Days"
                onChange={e => handleNumericChange(e.target.value, setFirstCommissionConfig)}
              />
            </div>
          </FormControl>
        )}
        <div className="w-full mt-5 flex flex-col gap-4 lg:flex-row lg:!flex-nowrap">
          {actionReward === 'FIRST_COMMISSION' && (
            <>
              <TextField
                className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
                size="medium"
                label="Days Config"
                value={firstCommissionConfig}
                placeholder="Enter Days"
                onChange={e => handleNumericChange(e.target.value, setFirstCommissionConfig)}
              />
              <TextField
                className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
                size="medium"
                label="Post Campaign Purchase Window Days"
                value={firstCommissionPostCampaignDays}
                placeholder="Enter Days (optional)"
                onChange={e => handleNumericChange(e.target.value, setFirstCommissionPostCampaignDays)}
              />
            </>
          )}
          {actionReward === 'SEGMENT_COMMISSION' && (
            <TextField
              className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
              size="medium"
              label="Min Days Since Enrollment"
              value={segmentCommissionConfig}
              placeholder="Enter Min Days"
              onChange={e => handleNumericChange(e.target.value, setSegmentCommissionConfig)}
            />
          )}
          {actionReward === 'DORMANT_PURCHASER' && (
            <TextField
              className="w-full lg:min-w-[150px] lg:w-auto text-field-label"
              size="medium"
              label="Min Days Since Last Commission"
              value={dormantPurchaserConfig}
              placeholder="Enter Min Days"
              onChange={e => handleNumericChange(e.target.value, setDormantPurchaserConfig)}
            />
          )}
          <DateTimeRangePicker
            className="w-full !mt-0 justify-end gap-4 [&>div>.MuiInputBase-root>input]:!font-montserrat [&>div>.MuiInputBase-root>input]:!text-sm [&>div>.MuiFormLabel-root]:!font-montserrat [&>div>.MuiFormLabel-root]:!text-sm [&>div]:!w-full flex-nowrap"
            startDate={startDate}
            endDate={endDate}
            startDateDisabled={isPresentCampaign}
            onStartDateChange={value => {
              setStartDate(value);
            }}
            onEndDateChange={value => {
              setEndDate(value);
            }}
          />
        </div>
        <div className={`flex flex-row w-full flex-nowrap ${(actionReward === 'FIRST_COMMISSION' || actionReward === 'SEGMENT_COMMISSION' || actionReward === 'DORMANT_PURCHASER') && 'lg:ml-[170px]'}`}>
          <div className="w-full">
            {startDate && (
              <>
                <p className="text-xs italic mt-2">
                  Pacific: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Los_Angeles')}
                </p>
                <p className="text-xs italic">
                  Mountain: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Denver')}
                </p>
                <p className="text-xs italic">
                  Central: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Chicago')}
                </p>
                <p className="text-xs italic">
                  Eastern: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/New_York')}
                </p>
              </>
            )}
          </div>

          <div className="w-full">
            {endDate && (
              <>
                <p className="text-xs italic mt-2">
                  Pacific: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Los_Angeles')}
                </p>
                <p className="text-xs italic">
                  Mountain: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Denver')}
                </p>
                <p className="text-xs italic">
                  Central: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Chicago')}
                </p>
                <p className="text-xs italic">
                  Eastern: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/New_York')}
                </p>
              </>
            )}
          </div>
        </div>
        <div className="w-full flex justify-center mt-10">
          <Button
            disabled={isCreatingCampaign || isUpdatingCampaign || !isFormReady}
            className={`px-5 py-4 font-semibold !text-sm ${
              isFormReady ? 'purple-rounded-button' : 'grey-rounded-button opacity-50 !cursor-not-allowed'
            }`}
            onClick={isInEditMode ? handleUpdateCampaign : handleCreateCampaign}
          >
            {isInEditMode ? 'Update Campaign' : 'Create Campaign'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ActionRewardsModal;
